"use client";

import React from "react";
import ExportedImage from "next-image-export-optimizer";
import { useTranslations } from "@/lib/i18n-client";
import { Stagger, StaggerItem, Floating } from "@/components/shared/motion";
import { Database, Share2, Sparkles } from "lucide-react";

/**
 * Coastal Freedom — 3 PHOTO benefit cards (vacation data-adequacy framing, NOT
 * price/coverage generics — anti-converge). Cards lead with "enough data for
 * your whole trip", hotspot/family sharing, and stress-free freedom — the
 * brand's decided thesis (strategy §3/§7/§8; research/07 CF1; CF3 fair-use
 * transparency). Each pairs a benefit with a coastal lifestyle photo frame
 * (cf-photo-wash placeholder until real beach photography is licensed).
 * Client / claim-safe (large/high-data + fair-use, no bare "unlimited").
 */
export default function BenefitCards() {
  const { t } = useTranslations();

  const cards = [
    { icon: Database, title: t("cf.ben.b1", "Enough data for your whole trip"), body: t("cf.ben.b1b", "Large-data vacation plans for maps, social, video calls and uploads — with fair-use terms you can actually see.") },
    { icon: Share2, title: t("cf.ben.b2", "Share the connection"), body: t("cf.ben.b2b", "Hotspot and tethering included, so couples and families can get online together.") },
    { icon: Sparkles, title: t("cf.ben.b3", "No bill shock, total freedom"), body: t("cf.ben.b3b", "No roaming fees and no surprise charges — just relaxed, connected vacation days.") },
  ];

  return (
    <section className="container mx-auto px-4 py-10">
      <Stagger as="div" className="grid md:grid-cols-3 gap-4" staggerChildren={0.14} amount={0.25}>
        {cards.map(({ icon: Icon, title, body }, index) => (
          <StaggerItem key={title} y={28} className="cf-card p-4 flex items-center gap-4 transition-all hover:-translate-y-1 hover:shadow-[0_18px_40px_-20px_hsl(200_70%_40%_/_0.45)]">
            <div className="flex-1">
              <Floating className="inline-flex items-center justify-center w-10 h-10 rounded-xl bg-primary/10 text-primary mb-2"><Icon className="w-5 h-5" /></Floating>
              <h3 className="font-bold text-foreground">{title}</h3>
              <p className="text-sm text-muted-foreground">{body}</p>
            </div>
            <div className="cf-photo relative h-24 w-24 shrink-0 overflow-hidden rounded-[1.35rem] border border-white/60 shadow-[0_12px_28px_-18px_hsl(200_70%_28%_/_0.55)]" aria-hidden="true">
              <ExportedImage
                src="/images/brands/coastal-freedom-travel-esim/coastal_hero_bg.webp"
                alt=""
                width={160}
                height={160}
                className="h-full w-full object-cover"
                style={{ objectPosition: index === 0 ? "22% center" : index === 1 ? "center center" : "74% center" }}
              />
              <div className="absolute inset-0 bg-[linear-gradient(180deg,transparent_0%,hsl(201_66%_12%_/_0.16)_100%)]" />
            </div>
          </StaggerItem>
        ))}
      </Stagger>
    </section>
  );
}
